<template>
  <aside class="pd-sidebar" v-if="!isLoading">
    <nav class="sidebar-list">
      <button
        v-for="(item, index) in sidebarItems"
        :key="index"
        class="sidebar-item"
        :class="{ active: activeIndex === index }"
        @click="setActive(index)"
      >
        <Icons
          v-if="item.iconType === 'component'"
          :name="item.icon"
          :color="activeIndex === index ? '#F4E' : '#242424'"
          size="md"
        />
        <span v-else class="icon">{{ item.icon }}</span>
        <span class="text">{{ item.text }}</span>
      </button>
    </nav>

    <div class="sidebar-footer">
      <button 
        class="go-reviews" 
        :class="{ disabled: isProductDisabled }"
        :disabled="isProductDisabled"
        @click="goToReviews"
      >
        <Icons name="ProductReview" :color="isProductDisabled ? '#B4B4B4' : '#1C1C1E'" size="md" />
        <span class="text">Ürün Yorumlarına Git</span>
      </button>
    </div>
  </aside>
  <ProductSidebarSkeleton v-else />
</template>

<script setup>
import { computed } from "vue";
import { useStore } from "vuex";
import { Icons } from "@n11/so-ui-components";
import ProductSidebarSkeleton from '@/components/Skeleton/ProductDetailSkeleton/ProductSidebarSkeleton.vue';
import { sendProductDetailEvent } from '@/utils/ga4/helper';
import { GA4_EVENT } from '@/utils/ga4/event';

const store = useStore();

const props = defineProps({
  nutritionInfoRequired: {
    type: Boolean,
    default: true,
  },
  productId: {
    type: [String, Number],
    required: true,
  },
  isLoading: {
    type: Boolean,
    required: true,
  },
  activeMenuIndex: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(["menu-changed"]);

const activeIndex = computed(() => props.activeMenuIndex);

const isProductDisabled = computed(() => store.getters['products/isProductDisabled']);

const baseSidebarItems = [
  {
    icon: "ProductDetailStatusIcon",
    iconType: "component",
    text: "Ürün & Satış Durumu",
  },
  {
    icon: "ProductDetailIcon",
    iconType: "component",
    text: "Ürün Bilgileri",
  },
  {
    icon: "ProductImagesIcon",
    iconType: "component",
    text: "Ürün Görselleri",
  },
  {
    icon: "SalesAndVariantIcon",
    iconType: "component",
    text: "Satış & Varyant Bilgileri",
  },
  {
    icon: "AdditionalInfoIcon",
    iconType: "component",
    text: "Ek Bilgiler",
  },
  {
    icon: "ProductFeatureIcon",
    iconType: "component",
    text: "Ürün Özellikleri",
  },
  {
    icon: "ProductDetailInfoIcon",
    iconType: "component",
    text: "Ürün Açıklaması",
  },
  {
    icon: "DeliveryTemplateIcon",
    iconType: "component",
    text: "Teslimat Şablonu",
  },
  {
    icon: "ProductSafetyInfoIcon",
    iconType: "component",
    text: "Ürün Güvenliği Bilgileri",
  },
];

const nutritionInfoItem = {
  icon: "ProductTagIcon",
  iconType: "component",
  text: "Besin Bilgileri ve Değerleri",
};

const sidebarItems = computed(() => {
  const items = [...baseSidebarItems];

  if (props.nutritionInfoRequired) {
    items.push(nutritionInfoItem);
  }

  return items;
});

const setActive = (index) => {
  emit("menu-changed", index, sidebarItems.value[index]);
};

const goToReviews = () => {
  sendProductDetailEvent(GA4_EVENT.BUTTON_CLICK, {
    button_name: 'Ürün Yorumlarına Git',
    button_type: 'Product Reviews',
    button_area: 'Page End',
    item_id: props.productId,
  });
  const url = `/selleroffice/product/products?pid=${props.productId}`;
  window.location.href = url;
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

.pd-sidebar {
  min-width: 262px;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
}

.sidebar-list {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.sidebar-list::-webkit-scrollbar {
  display: none;
}

.sidebar-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 12px;
  margin-bottom: 1px;
  background: $colorN00;
  cursor: pointer;
  transition: all 0.2s ease;
}

.sidebar-item:first-child {
  padding-top: 28px !important;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.sidebar-item:last-child {
  padding-bottom: 28px !important;
  margin-bottom: 0 !important;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
}

.sidebar-item:hover {
  background-color: $colorN10;
}

.sidebar-item .icon {
  font-size: 20px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sidebar-item .text {
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: -0.05px;
  white-space: nowrap;
}

.sidebar-item.active .text {
  color: $colorM50 !important;
  font-weight: 600 !important;
}

.sidebar-footer {
  margin-top: 12px;
}

.go-reviews {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 16px;
  border: 1px solid $colorN80;
  background: $colorN00;
  border-radius: 6px;
  gap: 2px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: 700;
  color: $colorN60;
}

.go-reviews:hover:not(:disabled) {
  background-color: $gray-12;
  border-color: $gray-12;
}

.go-reviews.disabled,
.go-reviews:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: $colorN40;
  border-color: $gray-19;
  .icon,
  .text {
    color: $colorN00 !important;
  }
}

.go-reviews .icon {
  font-size: 16px;
}

.go-reviews .text {
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 18px;
  letter-spacing: -0.05px;
  color: $colorN80;
}

/* Responsive Design */
@media (max-width: 768px) {
  .pd-sidebar {
    position: static;
    width: 100%;
    height: auto;
    flex-direction: column;
    background: $colorN00;
    margin-bottom: 16px;
  }

  .sidebar-list {
    display: flex;
    flex-direction: column;
    overflow: visible;
    gap: 0;
    padding: 0;
  }

  .sidebar-item {
    width: 100%;
    flex-direction: row;
    gap: 12px;
    padding: 16px 20px;
    text-align: left;
    border-radius: 0;
    background: $colorN00;
    border-bottom: 1px solid $white-4;
  }

  .sidebar-item:first-child {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    padding-top: 16px !important;
  }

  .sidebar-item:last-child {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
    padding-bottom: 16px !important;
    border-bottom: none;
  }

  .sidebar-item .text {
    font-size: 14px;
    line-height: 18px;
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    max-width: none;
  }

  .sidebar-footer {
    margin-top: 0;
    padding: 16px 20px;
  }

  .go-reviews {
    width: 100%;
    padding: 16px;
  }
}

@media (max-width: 960px) and (min-width: 769px) {
  .pd-sidebar {
    width: 220px;
  }

  .sidebar-item .text {
    font-size: 13px;
  }
}
</style>
